import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { useCallback, useRef, useState } from 'react'
import { chatApi } from '@/api/chat'
import type { AskAttachmentRef, ChatMessage, ChatTab, EstadoDoPlano, MessagesPage } from '@/api/chat'
import { useAuthStore } from '@/store/auth'
import { SseStreamCutError, streamAskInConversation } from '@/api/assistant'
import { aplicarEventoDePasso, type PassoAoVivo } from '@/pages/chat/passosAoVivo'

export function useConversations() {
  return useQuery({
    queryKey: ['conversations'],
    queryFn: () => chatApi.listConversations(),
    staleTime: 30_000,
  })
}

export function useConversationMessages(cid: number, tab?: ChatTab) {
  return useInfiniteQuery({
    queryKey: ['conversation-messages', cid, tab ?? null],
    queryFn: ({ pageParam }) =>
      chatApi.listMessages(cid, {
        before: pageParam as number | undefined,
        limit: 50,
        tab,
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 15_000,
    enabled: cid > 0,
  })
}

export function useConversationMembers(cid: number) {
  return useQuery({
    queryKey: ['conversation-members', cid],
    queryFn: () => chatApi.members(cid),
    staleTime: 60_000,
    enabled: cid > 0,
  })
}

export function useMessageInfo(cid: number, mid: number, enabled: boolean) {
  return useQuery({
    queryKey: ['message-info', cid, mid],
    queryFn: () => chatApi.messageInfo(cid, mid),
    enabled: enabled && cid > 0 && mid > 0,
    staleTime: 15_000,
  })
}

export function useAskBot(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      text,
      alertMessageId,
      shared,
      attachments,
    }: {
      text: string
      alertMessageId?: number
      shared?: boolean
      attachments?: { s3_key: string; filename: string; mime_type: string; size_bytes: number }[]
    }) => chatApi.askBot(cid, text, { alertMessageId, shared, attachments }),
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: 'Não foi possível enviar a mensagem.',
      })
    },
    onSuccess: () => {
      // Invalida o prefixo → cobre todas as tabs (atendente + global)
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
  })
}

/** Pergunta ao robô de alguém no grupo (TASK-12125): resposta só de leitura. */
export function useAskRoboNoGrupo(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ botUserId, text }: { botUserId: number; text: string }) =>
      chatApi.askRoboNoGrupo(cid, botUserId, text),
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: status === 404
          ? 'Esse robô não está mais neste grupo.'
          : 'Não foi possível perguntar ao robô.',
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

/**
 * Envio ao @Atendente com streaming SSE dos passos (Fase 2). Diferente do
 * `useAskBot` bloqueante, expõe `streaming` + `steps` (ferramentas invocadas ao
 * vivo) para renderizar a `LiveAssistantBubble`. Ao terminar (`done`) ou em erro,
 * invalida as mensagens da conversa → a bolha ao vivo dá lugar às mensagens
 * persistidas (com botões de ação/propostas). Sem auto-retry.
 */
export function useAskBotStream(cid: number) {
  const queryClient = useQueryClient()
  const [streaming, setStreaming] = useState(false)
  const [steps, setSteps] = useState<PassoAoVivo[]>([])
  const busyRef = useRef(false)

  const start = useCallback(
    async (
      text: string,
      opts?: {
        alertMessageId?: number
        shared?: boolean
        attachments?: AskAttachmentRef[]
        consultaSqlId?: number
      },
    ) => {
      if (busyRef.current) return
      busyRef.current = true
      setStreaming(true)
      setSteps([])

      // A pergunta entra no cache AGORA, não quando a resposta chega. Sem isto
      // ela some da tela durante a análise do @Atendente: o único momento em
      // que ela entrava era o `invalidateQueries` do `finally`, depois do
      // turno inteiro. O `usePostMessage` já fazia otimista; este caminho não.
      // Escreve por PREFIXO porque a tab é o 3º elemento da chave e a thread
      // humana (dm/org) não tem aba — mesma leitura que o handler de corte
      // de SSE abaixo já faz.
      const me = useAuthStore.getState().user
      const optimistic: ChatMessage = {
        id: -Date.now(),
        kind: 'human',
        severity: null,
        body: text,
        metadata: null,
        source_event_id: null,
        author_user_id: me?.id ?? null,
        author_name: me?.full_name ?? me?.email ?? null,
        author_avatar_url: me?.avatar_url ?? null,
        created_at: new Date().toISOString(),
        read: false,
      }
      // `await`, nao `void`: `cancelQueries` reverte a query cancelada ao
      // estado anterior, e essa reversao cai num microtask. Disparada sem
      // esperar, ela chegaria DEPOIS do `setQueryData` abaixo e apagaria a
      // otimista — a pergunta sumiria de novo, agora de forma intermitente.
      // Mesmo cuidado (e mesma ordem) do `onMutate` do `usePostMessage`.
      await queryClient.cancelQueries({ queryKey: ['conversation-messages', cid] })
      for (const [queryKey, data] of queryClient.getQueriesData<InfiniteData<MessagesPage>>({
        queryKey: ['conversation-messages', cid],
      })) {
        if (!data || data.pages.length === 0) continue
        const [first, ...rest] = data.pages
        queryClient.setQueryData(queryKey, {
          ...data,
          pages: [{ ...first, items: [optimistic, ...first.items] }, ...rest],
        })
      }

      streamAskInConversation(cid, text, opts, (ev) => {
        if (ev.type === 'tool' || ev.type === 'tool_fim') {
          setSteps((s) => aplicarEventoDePasso(s, ev))
        } else if (ev.type === 'error') {
          notifications.show({
            color: 'red',
            title: 'Erro',
            message: 'Não foi possível falar com o Atendente.',
          })
        }
      })
        .catch(async (err: unknown) => {
          // Corte de proxy não é falha do turno: desde o !1061 a resposta é
          // persistida pela thread que a produziu, e não pelo gerador SSE. Mentir
          // aqui treina o usuário a desconfiar de mensagem correta — foi o que
          // aconteceu no incidente de 05/08/2026. Reler, nunca repetir o POST: a
          // pergunta humana é commitada cedo e repetir duplicaria (INV-5 da spec).
          if (err instanceof SseStreamCutError) {
            await queryClient.refetchQueries({
              queryKey: ['conversation-messages', cid],
            })
            // A chave tem a tab como 3º elemento e a query é infinite: ler por
            // PREFIXO e achatar as páginas (campo `items`), como o invalidate
            // deste arquivo já faz.
            const respondeu = queryClient
              .getQueriesData<InfiniteData<MessagesPage>>({
                queryKey: ['conversation-messages', cid],
              })
              .some(([, data]) =>
                (data?.pages ?? []).some((p) =>
                  (p?.items ?? []).some(
                    (m) =>
                      m.kind === 'bot' &&
                      (err.humanMessageId === undefined ||
                        m.id > err.humanMessageId),
                  ),
                ),
              )
            if (respondeu) {
              notifications.show({
                color: 'yellow',
                title: 'Conexão instável',
                message: 'A conexão caiu, mas a resposta do Atendente foi salva.',
              })
              return
            }
          }
          notifications.show({
            color: 'red',
            title: 'Erro',
            message: 'Não foi possível enviar a mensagem.',
          })
        })
        .finally(() => {
          busyRef.current = false
          setStreaming(false)
          setSteps([])
          // Traz as mensagens persistidas (human + bot com propostas).
          queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
        })
    },
    [cid, queryClient],
  )

  return { streaming, steps, start }
}

type PostMessageVars = {
  text: string
  mentionUserIds?: number[]
  mentionAll?: boolean
  replyToMessageId?: number | null
  channel?: 'conversas' | 'alertas'
  attachments?: { s3_key: string; filename: string; mime_type: string; size_bytes: number }[]
}

/**
 * Chaves de cache (useInfiniteQuery) que devem receber a mensagem otimista.
 * Para thread humana o `tab` é `undefined` → key `null`. Para conversa de alerta
 * a mensagem entra no canal (`conversas`). Inserimos em ambas quando presentes
 * para que o remetente veja a própria mensagem IMEDIATAMENTE, sem esperar o
 * refetch.
 */
function optimisticTabKeys(channel?: 'conversas' | 'alertas'): (ChatTab | null)[] {
  const keys: (ChatTab | null)[] = [null]
  if (channel) keys.push(channel)
  return keys
}

export function usePostMessage(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: PostMessageVars) => {
      const { text, mentionUserIds, mentionAll, replyToMessageId, channel, attachments } = vars
      return chatApi.postMessage(cid, text, { mentionUserIds, mentionAll, replyToMessageId, channel, attachments })
    },
    // Optimistic update: insere a mensagem própria na página mais recente do
    // cache para o remetente vê-la na hora (o efeito de auto-scroll dispara em
    // messages.length). onError reverte; onSettled reconcilia com o servidor.
    onMutate: async (vars: PostMessageVars) => {
      const me = useAuthStore.getState().user
      const keys = optimisticTabKeys(vars.channel)
      const snapshots: { key: (ChatTab | null); data: InfiniteData<MessagesPage> | undefined }[] = []
      // id temporário negativo (evita colisão com ids reais do servidor).
      const tempId = -Date.now()
      const optimistic: ChatMessage = {
        id: tempId,
        kind: 'human',
        severity: null,
        body: vars.text,
        metadata: null,
        source_event_id: null,
        author_user_id: me?.id ?? null,
        author_name: me?.full_name ?? me?.email ?? null,
        author_avatar_url: me?.avatar_url ?? null,
        created_at: new Date().toISOString(),
        read: false,
      }

      for (const key of keys) {
        const queryKey = ['conversation-messages', cid, key]
        await queryClient.cancelQueries({ queryKey })
        const data = queryClient.getQueryData<InfiniteData<MessagesPage>>(queryKey)
        snapshots.push({ key, data })
        if (!data || data.pages.length === 0) continue
        // A primeira página guarda as mensagens mais recentes (items em ordem
        // decrescente; a lista as reverte). Prepend → vira a última na tela.
        const [first, ...rest] = data.pages
        const nextData: InfiniteData<MessagesPage> = {
          ...data,
          pages: [{ ...first, items: [optimistic, ...first.items] }, ...rest],
        }
        queryClient.setQueryData(queryKey, nextData)
      }

      return { snapshots }
    },
    onError: (_err, _vars, ctx) => {
      // Reverte o cache de todas as chaves tocadas.
      if (ctx?.snapshots) {
        for (const { key, data } of ctx.snapshots) {
          queryClient.setQueryData(['conversation-messages', cid, key], data)
        }
      }
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: 'Não foi possível enviar a mensagem.',
      })
    },
    onSettled: () => {
      // Invalida o prefixo ['conversation-messages', cid] → cobre todas as tabs;
      // reconcilia a mensagem otimista com a persistida (id real, ticks, etc.).
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
  })
}

export function useConfirmAction(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ messageId }: { messageId: number }) =>
      chatApi.confirmAction(cid, messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
  })
}

export function useDeclineAction(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ messageId }: { messageId: number }) =>
      chatApi.declineAction(cid, messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
  })
}

export function useDiscordarDoLaudo(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      messageId,
      motivo,
      comentario,
    }: {
      messageId: number
      motivo: string
      comentario?: string
    }) => chatApi.discordarDoLaudo(cid, messageId, { motivo, comentario }),
    onSuccess: () => {
      // Invalida as mensagens porque a marca da discordância vive no `metadata`
      // da mensagem — sem isto o cartão só mostraria o registro depois de um
      // reload, e o usuário concluiria que o clique não funcionou.
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
      notifications.show({
        color: 'inovareTeal',
        title: 'Registrado',
        message: 'A sua discordância foi registrada na trilha do diagnóstico.',
      })
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: 'Não foi possível registrar a discordância.',
      })
    },
  })
}

/** Escolhe uma alternativa do laudo: o servidor roda o dry-run e grava a proposta
 * como mensagem nova da conversa, que chega pela lista de mensagens. */
export function useEscolherAlternativa() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ cid, mid, verbo }: { cid: number; mid: number; verbo: string }) =>
      chatApi.escolherAlternativa(cid, mid, verbo),
    onSuccess: (_data, { cid }) => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: { mensagem?: string } } } })
        ?.response?.data?.detail
      notifications.show({
        color: 'red',
        title: 'Alternativa não disponível',
        message: detail?.mensagem ?? 'Não foi possível gerar o plano desta alternativa.',
      })
    },
  })
}

/** Estado da segunda aprovação do plano para quem olha. Enquanto `aguardando`,
 * relê a cada 15 s: o outro integrante pode aprovar a qualquer momento. */
export function useEstadoDoPlano(planId: string | null, enabled: boolean) {
  return useQuery<EstadoDoPlano>({
    queryKey: ['host-order-plan-estado', planId],
    queryFn: () => chatApi.estadoDoPlano(planId as string),
    enabled: enabled && !!planId,
    refetchInterval: (q) => (q.state.data?.estado === 'aguardando' ? 15_000 : false),
    staleTime: 5_000,
  })
}

export function useApplyChamadoAction(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ messageId, assigneeUserId, texto }: { messageId: number; assigneeUserId?: number; texto?: string }) =>
      chatApi.applyChamadoAction(cid, messageId, assigneeUserId, texto),
    // Também no erro: uma recusa pode fechar a proposta no servidor (`failed`), e o cartão
    // tem de sair do `pending` em vez de oferecer de novo o botão que já não vale.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
  })
}

export function useEditMessage(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ mid, text }: { mid: number; text: string }) =>
      chatApi.editMessage(cid, mid, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: 'Não foi possível editar a mensagem.',
      })
    },
  })
}

export function useDeleteMessage(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (mid: number) => chatApi.deleteMessage(cid, mid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: 'Não foi possível apagar a mensagem.',
      })
    },
  })
}

export function useHideMessage(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (mid: number) => chatApi.hideMessageForMe(cid, mid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: 'Não foi possível apagar a mensagem para você.',
      })
    },
  })
}

export function useSetReaction(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ mid, emoji }: { mid: number; emoji: string }) =>
      chatApi.setReaction(cid, mid, emoji),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: 'Não foi possível atualizar a reação.',
      })
    },
  })
}

export function useRemoveReaction(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ mid }: { mid: number }) => chatApi.removeReaction(cid, mid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: 'Não foi possível atualizar a reação.',
      })
    },
  })
}

export function useHideConversation(cid: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (hidden: boolean) => chatApi.hideConversation(cid, hidden),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['chat-human-conversations'] })
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: 'Não foi possível ocultar a conversa.',
      })
    },
  })
}

// Re-export ChatTab for convenience
export type { ChatTab }
